import { useEffect, useState } from 'react';
import { createQuickRangeState } from '../../../BankDataTable/useBankDateRange';
import { monthKeyToEndDate, monthKeyToStartDate } from '../../../../../../utils/dateUtils';

interface MonthState {
    startMonth: string;
    endMonth: string;
}

const EMPTY_MONTHS: MonthState = { startMonth: '', endMonth: '' };

/** Local, client-side Time Period filter for the drill-down drawer; resets via a plain effect keyed on `entityKey` since filtering is synchronous over in-memory data, with no fetch race to protect against. */
export function useDrillDownDateRange(entityKey: string, bounds: { floor: string; ceiling: string }) {
    const [{ startMonth, endMonth }, setMonths] = useState<MonthState>(EMPTY_MONTHS);

    useEffect(() => {
        setMonths(EMPTY_MONTHS);
    }, [entityKey]);

    const selectStartMonth = (month: string) => setMonths({ startMonth: month, endMonth });
    const selectEndMonth = (month: string) => setMonths({ startMonth, endMonth: month });
    const clear = () => setMonths(EMPTY_MONTHS);

    const { selectLastFY, selectLast12M, selectCurrentFY, activeQuickRange } = createQuickRangeState({
        startMonth,
        endMonth,
        setMonths,
    });

    const hasBothMonths = !!startMonth && !!endMonth;
    const applied = {
        startDate: hasBothMonths ? monthKeyToStartDate(startMonth) : '',
        endDate: hasBothMonths ? monthKeyToEndDate(endMonth) : '',
    };

    return {
        startMonth,
        endMonth,
        rangeApplied: hasBothMonths,
        applied,
        activeQuickRange,
        selectStartMonth,
        selectEndMonth,
        selectLastFY,
        selectLast12M,
        selectCurrentFY,
        clear,
        // Bounded to the entity's own transaction span — no separate API envelope to fetch,
        // the whole list is already in memory.
        startPickerMin: bounds.floor,
        startPickerMax: endMonth || bounds.ceiling,
        endPickerMin: startMonth || bounds.floor,
        endPickerMax: bounds.ceiling,
    };
}
